import { Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { needsRecovery, type DataRecoveryLead } from '@/lib/utils/dataRecoveryUtils';

// Rarely shown (legacy leads only) and pulls in the phone input + flags: keep it out of the shared chunks.
const DataRecoveryModal = lazy(() =>
  import('@/components/data-recovery/DataRecoveryModal').then((m) => ({ default: m.DataRecoveryModal })),
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  /** Shown while the session is checked; defaults to the spinner. A route whose next screen is a skeleton passes that skeleton. */
  loader?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  loader,
}: ProtectedRouteProps) {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Data recovery state
  const [needsDataRecovery, setNeedsDataRecovery] = useState(false);
  const [recoveryLead, setRecoveryLead] = useState<DataRecoveryLead | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

    if (!token) {
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }

    try {
      // Call the backend to verify token and get user role (includes lead data for clients)
      const response = await apiClient.get('/auth/me');
      const { user, lead } = response.data;

      setIsAuthenticated(true);
      setUserRole(user.role);

      // Check data recovery for clients - done client-side based on lead data
      if (user.role === 'client' && lead && needsRecovery(lead)) {
        setNeedsDataRecovery(true);
        setRecoveryLead(lead);
      }
    } catch (error) {
      // Token is invalid or expired
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setUserRole(null);

      // Clear invalid tokens
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setIsChecking(false);
    }
  };

  const handleRecoverySuccess = () => {
    setNeedsDataRecovery(false);
    setRecoveryLead(null);
  };

  if (isChecking) {
    if (loader) return <>{loader}</>;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
        <div className="text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-muted-foreground">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (requireSuperAdmin && userRole !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-2 font-heading">Acceso Denegado</h2>
          <p className="text-muted-foreground mb-4">
            No tienes permisos para acceder a esta sección. Solo Super Admins pueden acceder.
          </p>
          <button onClick={() => window.history.back()} className="text-primary hover:underline">
            Volver atrás
          </button>
        </div>
      </div>
    );
  }

  if (requireAdmin && userRole !== 'super_admin' && userRole !== 'manager_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-2 font-heading">Acceso Denegado</h2>
          <p className="text-muted-foreground mb-4">
            No tienes permisos para acceder al panel de administración.
          </p>
          <button onClick={() => window.history.back()} className="text-primary hover:underline">
            Volver atrás
          </button>
        </div>
      </div>
    );
  }

  // BLOCKING: Show data recovery modal for clients with missing phone data
  if (needsDataRecovery && recoveryLead) {
    return (
      <Suspense fallback={null}>
        <DataRecoveryModal
          lead={recoveryLead}
          onSuccess={handleRecoverySuccess}
        />
      </Suspense>
    );
  }

  return <>{children}</>;
}
